import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';
import { AreaChart } from '@mantine/charts';
import { SimpleGrid, Stack } from '@mantine/core';
import type { MonthRevenue, RevenueOverview } from 'src/secret-menu/billing/hooks/useRevenue';
import type { UsageMetricRow } from '../aggregate';
import {
  buildUsageModel,
  monthLabelOf,
  previousMonth,
  type UsageModel,
} from '../overview/usageModel';
import { BilledUnitsStat, formatDay, FullAdoptionStat, MrrStat, sinceText } from './RevenueStats';
import TrendChart from './TrendChart';
import { ChangeArrow, formatRevenue, formatUnits, Stat } from './ui';

type Props = {
  model: UsageModel;
  month: string;
  overview: RevenueOverview;
  history: readonly MonthRevenue[];
  today: Date;
  /** Each firm's billing, for billed families against stated families. */
  firms: readonly BillingDetail[];
  /** The daily usage rows the model was built from, to compare a month in progress fairly. */
  rows: readonly UsageMetricRow[];
};

/**
 * ABC on the same days of an earlier month: for a month in progress, the 1st to today's date, so
 * a half-finished month is not set against a whole one.
 */
function abcOnSameDays(rows: readonly UsageMetricRow[], month: string, today: Date): number {
  const lastDay = today.getUTCDate();
  const sameDays = rows.filter(
    row => row.date.startsWith(month) && Number(row.date.slice(8, 10)) <= lastDay,
  );
  return buildUsageModel(sameDays, today)?.platform.get(month)?.abc ?? 0;
}

/**
 * Every client added to Afternoon so far, from the same running total as the Total clients chart
 * below, compared with where it stood at the end of last month.
 */
function ClientsStat({ model, today }: Pick<Props, 'model' | 'today'>) {
  const latest = model.months[model.months.length - 1];
  const before = model.months[model.months.length - 2];
  const now = model.cumulativeClients.get(latest) ?? 0;
  const lastMonth = before === undefined ? null : (model.cumulativeClients.get(before) ?? 0);
  const trend = model.months.slice(-12).map(month => ({
    month: model.monthLabels.get(month) ?? month,
    clients: model.cumulativeClients.get(month) ?? 0,
  }));
  return (
    <Stat
      label="Clients on Afternoon"
      caption={`As of ${formatDay(today)}`}
      value={formatUnits(now)}
      change={
        lastMonth === null ? undefined : (
          <ChangeArrow current={now} previous={lastMonth} period="MoM" />
        )
      }
      detail={lastMonth === null ? 'No previous month to compare yet' : undefined}
      hint="allClients"
      footer={
        trend.length < 2 ? undefined : (
          <AreaChart
            h={80}
            data={trend}
            dataKey="month"
            series={[{ name: 'clients', label: 'Clients on Afternoon', color: 'blue.6' }]}
            curveType="linear"
            withDots={false}
            withYAxis={false}
            gridAxis="none"
            valueFormatter={formatUnits}
          />
        )
      }
    />
  );
}

/**
 * The headline numbers for the all-hands. Revenue today and customer usage lead as the two large
 * cards; the supporting figures follow, ordered by how directly they drive revenue. ABC follows
 * the month picker; the rest are today's and say so.
 */
export default function KpiHeadline({
  model,
  month,
  overview,
  history,
  today,
  firms,
  rows,
}: Props) {
  const prev = previousMonth(model, month);
  const usageNow = model.platform.get(month)?.abc ?? 0;
  const excludingNew = model.platform.get(month)?.usage ?? 0;
  const inProgress = month === model.partialMonth;
  const monthLabel = model.monthLabels.get(month) ?? month;
  const prevLabel = prev ? (model.monthLabels.get(prev) ?? prev) : '';
  let usageBefore: number | null = null;
  if (prev) {
    usageBefore = inProgress
      ? abcOnSameDays(rows, prev, today)
      : (model.platform.get(prev)?.abc ?? 0);
  }

  let usageDetail = 'No earlier month to compare';
  if (usageBefore !== null) {
    usageDetail = inProgress
      ? `So far this month, against the same days of ${prevLabel}`
      : sinceText(usageNow, usageBefore, 'the month before');
  }

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <MrrStat
          overview={overview}
          history={history}
          today={today}
          size="headline"
          aside={
            <TrendChart
              title="Adviser firms' monthly revenue"
              points={history.map(entry => ({
                label: monthLabelOf(entry.month.slice(0, 7)),
                value: entry.adviserMrrPence,
              }))}
              format={formatRevenue}
              color="teal.6"
            />
          }
        />
        <Stat
          size="headline"
          label="Actions by customers (ABC)"
          caption={inProgress ? `${monthLabel} · month in progress` : monthLabel}
          value={formatUnits(usageNow)}
          change={
            usageBefore === null ? undefined : (
              <ChangeArrow current={usageNow} previous={usageBefore} period="MoM" />
            )
          }
          rows={[{ label: 'Excluding adding new clients', value: formatUnits(excludingNew) }]}
          detail={usageDetail}
          hint="actionsByCustomers"
          aside={
            <TrendChart
              title="Actions by customers per month"
              points={model.months.map(key => ({
                label: model.monthLabels.get(key) ?? key,
                value: model.platform.get(key)?.abc ?? 0,
              }))}
              format={formatUnits}
              color="orange.6"
            />
          }
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <FullAdoptionStat overview={overview} />
        <BilledUnitsStat overview={overview} history={history} today={today} firms={firms} />
        <ClientsStat model={model} today={today} />
      </SimpleGrid>
    </Stack>
  );
}
